import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Empty, Table, Typography } from 'antd';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import { Button } from '~/components';
import EditBusinessUnitFamily from './EditBusinessUnitFamily';
import {
  deleteFamilies,
  deleteRoleFamilies,
  getFamiliesById,
  updateFamilies,
  updateRoleFamilies,
} from '~/store/fetchActions/fetchFamilies';
import AddRoleOrSubFamily from './AddRoleOrSubFamily';
import { selectLoadingState } from '~/store/selectors';

const { Link } = Typography;

export default function BusinessUnitFamiliesTable({
  families,
  buId,
  familyType,
}) {
  const [isEditFamilyModalVisible, setEditFamilyModalVisible] = useState(false);
  const [handleIsRole, setHandleIsRole] = useState(false);
  const [
    isAddRoleOrSubFamilyModalVisible,
    setAddRoleOrSubFamilyModalVisible,
  ] = useState(false);
  const [currentFamily, setCurrentFamily] = useState();
  const [currentRole, setCurrentRole] = useState();
  const [currentRoleList, setCurrentRoleList] = useState();
  const [currentFamilyId, setCurrentFamilyId] = useState();
  const dispatch = useDispatch();
  const { isLoading } = useSelector(selectLoadingState);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [expandedRowSubFamilyKeys, setExpandedRowSubFamilyKeys] = useState([]);
  const [currentExpandedFamily, setCurrentExpandedFamily] = useState();
  const [isSubFamilyDetailedEdit, setIsSubFamilyDetailedEdit] = useState(false);
  const [rolesLength, setRolesLength] = useState(0);

  useEffect(() => {
    if (currentExpandedFamily) {
      const result = families.find(
        family => family.name === currentExpandedFamily.name,
      );
      if (result) {
        setRolesLength(result.roles.length);
      }
    }
  }, [families, currentExpandedFamily]);

  const handleExpandFamily = (expanded, family) => {
    if (expanded) {
      setCurrentExpandedFamily(family);
      setRolesLength(family.roles.length || 0);
      setExpandedRowSubFamilyKeys([]);
    }
    setExpandedRowKeys(expanded ? [family.id] : []);
  };

  const handleExpandSubFamily = (expanded, subFamily) => {
    if (expanded) {
      setRolesLength(subFamily.roles.length || 0);
      setCurrentExpandedFamily(subFamily);
    }
    setExpandedRowSubFamilyKeys(expanded ? [subFamily.id] : []);
  };

  const handleCloseEditFamilyModal = useCallback(() => {
    setEditFamilyModalVisible(false);
  }, []);

  const handleCloseAddRoleOrSubFamily = useCallback(() => {
    setAddRoleOrSubFamilyModalVisible(false);
    setCurrentRole(null);
  }, []);

  const sortHierarchyRoleUp = useCallback(
    (hierarchyEdit, roleEdit) => {
      // se for de baixo pra cima
      if (roleEdit && hierarchyEdit > 1) {
        // registro clicado
        dispatch(
          updateRoleFamilies(
            roleEdit.name,
            roleEdit.id,
            buId,
            roleEdit.description,
            roleEdit.competence,
            hierarchyEdit - 1,
          ),
        );
      }
    },
    [buId, dispatch],
  );

  const sortHierarchyRoleDown = useCallback(
    (hierarchyEdit, roleEdit) => {
      // se for de cima pra baixo
      if (roleEdit && rolesLength > hierarchyEdit) {
        // registro clicado
        dispatch(
          updateRoleFamilies(
            roleEdit.name,
            roleEdit.id,
            buId,
            roleEdit.description,
            roleEdit.competence,
            hierarchyEdit + 1,
          ),
        );
      }
    },
    [buId, dispatch, rolesLength],
  );

  const handleDeleteFamily = useCallback(
    id => {
      dispatch(deleteFamilies(id, buId));
      setCurrentExpandedFamily(null);
      setRolesLength(0);
    },
    [dispatch, buId],
  );

  const handleDeleteRoleFamily = useCallback(
    id => {
      dispatch(deleteRoleFamilies(id, buId));
    },
    [dispatch, buId],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Nome da Família',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Ações',
        align: 'end',
        render: family => {
          const { id, hierarchy } = family;
          async function openModalAddRoleOrSubFamily() {
            setCurrentRole(null);
            setHandleIsRole(false);
            setCurrentFamilyId(id);
            await setCurrentExpandedFamily(family);
            setAddRoleOrSubFamilyModalVisible(true);
            dispatch(getFamiliesById(id));
            setCurrentFamily(family);
          }
          return (
            <>
              <Link
                key={`add-family-${id}`}
                data-testid={`btn-add-family-${id}`}
                onClick={() => {
                  openModalAddRoleOrSubFamily();
                }}
              >
                <Button type="add" style={{ marginRight: 24 }} />
              </Link>
              <Link
                key={`edit-family-${id}`}
                data-testid={`btn-edit-family-${id}`}
                onClick={() => {
                  setCurrentFamily(family);
                  setIsSubFamilyDetailedEdit(family.subFamilies.length > 0);
                  setEditFamilyModalVisible(true);
                }}
              >
                <Button type="edit" style={{ marginRight: 24 }} />
              </Link>

              <Button
                type="delete"
                style={{ marginRight: 24 }}
                handleDelete={() => handleDeleteFamily(id)}
              />
            </>
          );
        },
      },
    ],
    [handleDeleteFamily, dispatch],
  );

  const subFamilyColumns = useMemo(
    () => [
      {
        title: 'Sub-Famílias',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Ações',
        align: 'end',
        render: subFamily => {
          const { id, hierarchy } = subFamily;
          async function openModalAddRoleOrSubFamily() {
            setHandleIsRole(false);
            setCurrentRole(null);
            setCurrentFamilyId(id);
            await setCurrentExpandedFamily(subFamily);
            setAddRoleOrSubFamilyModalVisible(true);
            dispatch(getFamiliesById(id));
            setCurrentFamily(subFamily);
          }
          return (
            <>
              <Link
                key={`add-subFamily-${id}`}
                data-testid={`btn-add-subFamily-${id}`}
                onClick={() => {
                  openModalAddRoleOrSubFamily();
                }}
              >
                <Button type="add" style={{ marginRight: 24 }} />
              </Link>
              <Link
                key={`edit-subFamily-${id}`}
                data-testid={`btn-edit-subFamily-${id}`}
                onClick={() => {
                  setEditFamilyModalVisible(true);
                  setIsSubFamilyDetailedEdit(false);
                  setCurrentFamily(subFamily);
                }}
              >
                <Button type="edit" style={{ marginRight: 24 }} />
              </Link>
              <Button
                type="delete"
                style={{ marginRight: 24 }}
                handleDelete={() => handleDeleteFamily(id)}
              />
            </>
          );
        },
      },
    ],
    [handleDeleteFamily, dispatch],
  );

  const rolesColumns = useMemo(
    () => [
      {
        title: 'Cargos',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Ações',
        align: 'end',
        render: role => {
          const { id, hierarchy } = role;

          return (
            <>
              <Link
                key={`sort-up-role-${id}`}
                data-testid={`btn-sort-up-role-${id}`}
                onClick={() => {
                  sortHierarchyRoleUp(hierarchy, role);
                }}
              >
                <Button type="sort-up" style={{ marginRight: 24 }} />
              </Link>
              <Link
                key={`sort-down-role-${id}`}
                data-testid={`btn-sort-down-role-${id}`}
                onClick={() => {
                  sortHierarchyRoleDown(hierarchy, role);
                }}
              >
                <Button type="sort-down" style={{ marginRight: 24 }} />
              </Link>
              <Link
                key={`edit-role-${role}`}
                data-testid={`btn-edit-role-${role}`}
                onClick={() => {
                  setCurrentRole(role);
                  setAddRoleOrSubFamilyModalVisible(true);
                }}
              >
                <Button type="edit" style={{ marginRight: 24 }} />
              </Link>
              <Button
                type="delete"
                style={{ marginRight: 24 }}
                handleDelete={() => handleDeleteRoleFamily(id)}
              />
            </>
          );
        },
      },
    ],
    [handleDeleteRoleFamily, sortHierarchyRoleUp, sortHierarchyRoleDown],
  );

  const rolesSubFamilyColumns = useMemo(
    () => [
      {
        title: 'Cargos',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Ações',
        align: 'end',
        render: roleSubFamily => {
          const { id, hierarchy } = roleSubFamily;

          return (
            <>
              <Link
                key={`sort-up-role-subFamily-${id}`}
                data-testid={`btn-sort-up-role-subFamily-${id}`}
                onClick={() => {
                  sortHierarchyRoleUp(hierarchy, roleSubFamily);
                }}
              >
                <Button type="sort-up" style={{ marginRight: 24 }} />
              </Link>
              <Link
                key={`sort-down-role-subFamily-${id}`}
                data-testid={`btn-sort-down-role-subFamily-${id}`}
                onClick={() => {
                  sortHierarchyRoleDown(hierarchy, roleSubFamily);
                }}
              >
                <Button type="sort-down" style={{ marginRight: 24 }} />
              </Link>
              <Link
                key={`edit-role-subFamily-${roleSubFamily}`}
                data-testid={`btn-edit-role-subFamily-${roleSubFamily}`}
                onClick={() => {
                  setCurrentRole(roleSubFamily);
                  setAddRoleOrSubFamilyModalVisible(true);
                }}
              >
                <Button type="edit" style={{ marginRight: 24 }} />
              </Link>
              <Button
                type="delete"
                style={{ marginRight: 24 }}
                handleDelete={() => handleDeleteRoleFamily(id)}
              />
            </>
          );
        },
      },
    ],
    [handleDeleteRoleFamily, sortHierarchyRoleUp, sortHierarchyRoleDown],
  );

  return (
    <>
      <Table
        columns={columns}
        dataSource={families}
        loading={isLoading}
        expandable={{
          expandedRowRender: record => {
            return (
              <>
                {record?.roles?.length === 0 &&
                  record?.subFamilies?.length <= 0 && (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Não há dados registrados."
                    />
                  )}
                {record.roles.length > 0 && (
                  <Table
                    columns={rolesColumns}
                    dataSource={record.roles}
                    pagination={false}
                  />
                )}
                {record.subFamilies && record.subFamilies.length > 0 && (
                  <Table
                    columns={subFamilyColumns}
                    dataSource={record.subFamilies}
                    pagination={false}
                    expandable={{
                      expandedRowRender: record2 => {
                        return (
                          <>
                            {record2.roles.length > 0 && (
                              <Table
                                columns={rolesSubFamilyColumns}
                                dataSource={record2.roles}
                                pagination={false}
                                onChange={handleExpandSubFamily}
                              />
                            )}
                          </>
                        );
                      },
                      rowExpandable: record2 =>
                        record2.roles && record2.roles.length > 0,
                      expandedRowKeys: expandedRowSubFamilyKeys,
                      onExpand: handleExpandSubFamily,
                    }}
                    rowKey="id"
                  />
                )}
              </>
            );
          },
          rowExpandable: record =>
            (record.roles && record.roles.length > 0) ||
            (record.subFamilies && record.subFamilies.length > 0),
          expandedRowKeys,
          onExpand: handleExpandFamily,
        }}
        rowKey="id"
      />
      <EditBusinessUnitFamily
        isVisible={isEditFamilyModalVisible}
        setVisible={handleCloseEditFamilyModal}
        family={currentFamily}
        isAddFamily={false}
        isSubFamilyDetailedEdit={isSubFamilyDetailedEdit}
        familyType={familyType}
      />
      <AddRoleOrSubFamily
        isVisible={isAddRoleOrSubFamilyModalVisible}
        setVisible={handleCloseAddRoleOrSubFamily}
        familyId={currentFamilyId}
        currentFamilyExpanded={currentExpandedFamily}
        isRole={handleIsRole}
        role={currentRole}
        familyList={currentFamily}
        familyType={familyType}
      />
    </>
  );
}

BusinessUnitFamiliesTable.propTypes = {
  families: PropTypes.arrayOf(PropTypes.object),
  buId: PropTypes.string,
  familyType: PropTypes.string.isRequired,
};

BusinessUnitFamiliesTable.defaultProps = {
  families: [],
  buId: '',
};
